import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Paper,
  Divider,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  SelectChangeEvent,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  PhotoCamera,
  Save,
  Cancel,
  Delete,
  MedicalServices,
  BrokenImage,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { servicesService } from './services.service';
import { Service } from './services.dto';
import { DEFAULT_CURRENCY } from '../../constants';
import { formatDate } from '@/helpers';

import { useToast } from '../../contexts/ToastContext';
import {
  extractApiErrorMessage,
  mapApiErrorToField,
} from '../../utils/apiErrorMapping';
const EditService = () => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const { token, logout } = useAuth();
  const [formData, setFormData] = useState({
    sortOrder: 0,
    title: '',
    description: '',
    shortDescription: '',
    price: '',
    status: 1, // 1 = Available, 0 = Unavailable, 2 = Maintenance
    duration: '',
    preparation: '',
    image: '',
    image_main: '',
    parent_id: 0, // 0 = Parent service, or specific parent service ID
    createdAt: '',
    isAvailable: 1, // 1 = Available, 0 = Unavailable
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [parentServices, setParentServices] = useState<
    Array<{ id: number; title: string }>
  >([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  // Set when the stored image URL fails to actually load (broken/stale
  // URL, wrong host, deleted file, etc.) - the browser's own broken-image
  // icon gives no indication anything is wrong, so this surfaces it
  // explicitly instead of silently showing nothing.
  const [imageLoadError, setImageLoadError] = useState(false);
  const [imageMainFile, setImageMainFile] = useState<File | null>(null);
  const [imageMainPreview, setImageMainPreview] = useState<string>('');

  const statuses = [
    { value: 1, label: 'Active' },
    { value: 0, label: 'Inactive' },
  ];

  const isAvailables = [
    { value: 1, label: 'Available' },
    { value: 0, label: 'Unavailable' },
  ];

  // Set up logout callback and fetch data
  useEffect(() => {
    // Set up logout callback for session expired
    servicesService.setLogoutCallback(logout);

    const fetchData = async () => {
      if (!token || !id) return;

      try {
        setLoading(true);

        // Fetch service data and parent services in parallel
        const [service, parentServicesData] = await Promise.all([
          servicesService.getService(token, parseInt(id)),
          servicesService.getParentServices(token),
        ]);

        console.log(
          'Fetched parent services in EditService:',
          parentServicesData
        );
        console.log('Fetched service data:', service);

        setFormData({
          title: service.title,
          description: service.description,
          shortDescription: service.shortDescription || '',
          price: service.price ? String(service.price) : '',
          sortOrder: service.sortOrder,
          status: service.status,
          duration: service.duration ? String(service.duration) : '',
          preparation: service.preparation || '',
          image: service.image,
          image_main: service.image_main || '',
          parent_id: service.parent_id || 0,
          createdAt: service.createdAt || '',
          isAvailable: service.isAvailable,
        });

        // Set image preview if service has an image
        if (service.image) {
          setImagePreview(service.image);
        }

        // Set image_main preview if service has an image_main
        if (service.image_main) {
          setImageMainPreview(service.image_main);
        }

        setParentServices(parentServicesData);
      } catch (err) {
        if (err instanceof Error) {
          showError(err.message);
        } else {
          showError('Failed to fetch service data');
        }
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, id, navigate, logout]);

  const handleChange =
    (field: keyof typeof formData) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent
    ) => {
      let value: string | number = (event.target as HTMLInputElement).value;
      if (
        field === 'status' ||
        field === 'parent_id' ||
        field === 'isAvailable' ||
        field === 'sortOrder'
      ) {
        value = Number(value);
      }
      setFormData({ ...formData, [field]: value });
      if (errors[field as string]) {
        setErrors({ ...errors, [field as string]: '' });
      }
    };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showError('Image size should be less than 5MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        showError('Please upload a valid image file');
        return;
      }

      setImageFile(file);
      setImageLoadError(false);

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Clear image error if exists
      if (errors.image) {
        setErrors({ ...errors, image: '' });
      }
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    setImageLoadError(false);
    setFormData({ ...formData, image: '' });
  };

  const handleImageMainChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showError('Image size should be less than 5MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        showError('Please upload a valid image file');
        return;
      }

      setImageMainFile(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageMainPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Clear image error if exists
      if (errors.image_main) {
        setErrors({ ...errors, image_main: '' });
      }
    }
  };

  const handleRemoveImageMain = () => {
    setImageMainFile(null);
    setImageMainPreview('');
    setFormData({ ...formData, image_main: '' });
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Service title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.shortDescription.trim()) {
      newErrors.shortDescription = 'Short description is required';
    } else if (
      formData.shortDescription.trim().length < 130 ||
      formData.shortDescription.trim().length > 150
    ) {
      // maxLength=150 on the field only stops typing past 150 (and
      // silently truncates a paste with no feedback) - it never enforced
      // the 130 minimum the helper text also promises.
      newErrors.shortDescription = `Short description must be 130-150 characters (currently ${formData.shortDescription.trim().length})`;
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Valid price is required';
    }

    if (!formData.duration || !String(formData.duration).trim()) {
      newErrors.duration = 'Duration is required';
    } else if (
      !Number.isFinite(Number(formData.duration)) ||
      Number(formData.duration) <= 0
    ) {
      // '0' and '00' both pass a plain "is it empty" check but aren't a
      // real duration - a scan can't take 0 minutes.
      newErrors.duration = 'Duration must be greater than 0 minutes';
    }

    if (!imageFile && !formData.image) {
      newErrors.image = 'Service image is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      showError('Please fix all validation errors before submitting');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!token || !id) return;

    setSaving(true);
    try {
      // If there's an image file or image_main file, use FormData
      if (imageFile || imageMainFile) {
        const submitFormData = new FormData();
        submitFormData.append('title', formData.title);
        submitFormData.append('description', formData.description);
        submitFormData.append('shortDescription', formData.shortDescription);
        submitFormData.append('price', formData.price);
        submitFormData.append(
          'sortOrder',
          String(formData.sortOrder as unknown as number)
        );
        submitFormData.append('discount', '0');
        submitFormData.append('status', String(formData.status));
        submitFormData.append('duration', formData.duration);
        submitFormData.append('preparation', formData.preparation || '');
        submitFormData.append('parent_id', String(formData.parent_id));
        submitFormData.append('isAvailable', String(formData.isAvailable));

        if (imageFile) {
          submitFormData.append('image', imageFile);
        }

        if (imageMainFile) {
          submitFormData.append('image_main', imageMainFile);
        }

        console.log('Submitting FormData with image:', imageFile?.name);
        console.log(
          'Submitting FormData with image_main:',
          imageMainFile?.name
        );
        console.log('FormData contents:');
        for (const [key, value] of submitFormData.entries()) {
          console.log(key, value);
        }

        await servicesService.updateServiceWithImage(
          token,
          parseInt(id),
          submitFormData
        );
      } else {
        // No image file, use regular JSON update
        const serviceData: Partial<Service> = {
          title: formData.title,
          description: formData.description,
          shortDescription: formData.shortDescription,
          sortOrder: formData.sortOrder,
          image: formData.image,
          image_main: formData.image_main,
          price: parseFloat(formData.price),
          discount: '0',
          status: formData.status,
          duration: formData.duration,
          preparation: formData.preparation,
          parent_id: formData.parent_id,
          isAvailable: formData.isAvailable,
        };

        await servicesService.updateService(token, parseInt(id), serviceData);
      }

      // Was: showSuccess() here immediately, *then* navigate 1.5s later
      // with a location.state.message that made the listing page show its
      // own toast too - the same success popped up 2-3 times (worse in
      // dev, where StrictMode double-invokes the listing page's effect).
      // One source of truth: only the listing page shows it, right when
      // the user actually lands there.
      navigate('/services', {
        state: {
          message: 'Service updated successfully',
          severity: 'success',
        },
      });
    } catch (err) {
      const message = extractApiErrorMessage(err) || 'Failed to update service';
      const { field } = mapApiErrorToField(message, Object.keys(formData));
      if (field) {
        // Show it inline under the field it's actually about, same as
        // client-side validation - not just a toast the user can't map
        // to an input.
        setErrors(prev => ({ ...prev, [field]: message }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      showError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Return to wherever the listing was left (search term, page number)
    // rather than a bare /services, which always reset it - same fix as
    // the header back button below.
    navigate(-1);
  };

  const handleDelete = async () => {
    if (!token || !id) return;

    try {
      await servicesService.deleteService(token, parseInt(id));
      showSuccess('Service deleted successfully!');

      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (err) {
      if (err instanceof Error) {
        showError(err.message);
      } else {
        showError('Failed to delete service');
      }
    }
    setDeleteDialog(false);
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 1:
        return 'success';
      case 0:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case 1:
        return 'Active';
      case 0:
        return 'Inactive';
      default:
        return 'Unknown';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading service data...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                <ArrowBack />
              </IconButton>
              <Edit color="primary" sx={{ mr: 1 }} />
              <Typography variant="h4" component="h1">
                Edit Service
              </Typography>
            </Box>
            <Chip
              label={getStatusText(formData.status)}
              color={getStatusColor(formData.status)}
              variant="outlined"
            />
          </Box>
          <Typography variant="body1" color="text.secondary">
            Update service information and settings
          </Typography>
        </Paper>

        <Grid container spacing={3}>
          {/* Service Image Section */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Service Image
                </Typography>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: imagePreview ? 'primary.main' : 'grey.300',
                    borderRadius: 2,
                    p: imagePreview ? 0 : 4,
                    textAlign: 'center',
                    mb: 2,
                    minHeight: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {imagePreview && !imageLoadError ? (
                    <>
                      <Box
                        component="img"
                        src={imagePreview}
                        alt="Service preview"
                        onError={() => setImageLoadError(true)}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      <IconButton
                        onClick={handleRemoveImage}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'background.paper',
                          '&:hover': {
                            bgcolor: 'error.main',
                            color: 'white',
                          },
                        }}
                      >
                        <Cancel />
                      </IconButton>
                    </>
                  ) : imagePreview && imageLoadError ? (
                    <Box sx={{ px: 2 }}>
                      <BrokenImage
                        sx={{ fontSize: 48, color: 'error.main', mb: 1 }}
                      />
                      <Typography variant="body2" color="error">
                        This image failed to load - the stored URL appears
                        broken.
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ wordBreak: 'break-all', display: 'block' }}
                      >
                        {imagePreview}
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <MedicalServices
                        sx={{ fontSize: 48, color: 'grey.400', mb: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Upload service image
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<PhotoCamera />}
                  component="label"
                  fullWidth
                >
                  {imagePreview ? 'Change Image' : 'Upload Image'}
                  <input
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={handleImageChange}
                  />
                </Button>
                {errors.image && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    {errors.image}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ fontWeight: 500, lineHeight: 1.6 }}
                >
                  Image should be <strong>404*302px</strong>. Use JPG or PNG
                  format under 500 KB before upload.
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Created on
                </Typography>
                <Typography variant="h6">
                  {formatDate(formData.createdAt)}
                </Typography>
              </CardContent>
            </Card>

            {/* Main Image Section */}
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Main Service Image
                </Typography>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: imageMainPreview ? 'primary.main' : 'grey.300',
                    borderRadius: 2,
                    p: imageMainPreview ? 0 : 4,
                    textAlign: 'center',
                    mb: 2,
                    minHeight: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {imageMainPreview ? (
                    <>
                      <Box
                        component="img"
                        src={imageMainPreview}
                        alt="Main service preview"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      <IconButton
                        onClick={handleRemoveImageMain}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'background.paper',
                          '&:hover': {
                            bgcolor: 'error.main',
                            color: 'white',
                          },
                        }}
                      >
                        <Cancel />
                      </IconButton>
                    </>
                  ) : (
                    <Box>
                      <MedicalServices
                        sx={{ fontSize: 48, color: 'grey.400', mb: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Upload main service image
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<PhotoCamera />}
                  component="label"
                  fullWidth
                >
                  {imageMainPreview ? 'Change Main Image' : 'Upload Main Image'}
                  <input
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={handleImageMainChange}
                  />
                </Button>
                {errors.image_main && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    {errors.image_main}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ fontWeight: 500, lineHeight: 1.6 }}
                >
                  Image should be <strong>717*717px</strong>. Use JPG or PNG
                  format under 500 KB before upload.
                </Typography>
              </CardContent>
            </Card>

            {/* Service Settings */}
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Service Settings
                </Typography>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={String(formData.status)}
                    label="Status"
                    onChange={handleChange('status')}
                  >
                    {statuses.map(status => (
                      <MenuItem key={status.value} value={String(status.value)}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Is Available</InputLabel>
                  <Select
                    value={String(formData.isAvailable)}
                    label="Is Available"
                    onChange={handleChange('isAvailable')}
                  >
                    {isAvailables.map(status => (
                      <MenuItem key={status.value} value={String(status.value)}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </Card>

            {/* Danger Zone */}
            <Card
              sx={{ mt: 2, border: '1px solid', borderColor: 'error.light' }}
            >
              <CardContent>
                <Typography variant="h6" color="error" gutterBottom>
                  Danger Zone
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Once you delete a service, there is no going back. Please be
                  certain.
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setDeleteDialog(true)}
                  fullWidth
                >
                  Delete Service
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Form Section */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Grid container spacing={3}>
                    {/* Parent Service Selection */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Service Hierarchy
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Parent Service</InputLabel>
                        <Select
                          value={String(formData.parent_id)}
                          label="Parent Service"
                          onChange={handleChange('parent_id')}
                          disabled={loading}
                        >
                          <MenuItem value={0}>
                            <em>Parent</em>
                          </MenuItem>
                          {parentServices.map(service => (
                            <MenuItem key={service.id} value={service.id}>
                              {service.title}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography variant="caption" color="textSecondary">
                        Found {parentServices.length} parent services
                      </Typography>
                    </Grid>

                    {/* Basic Information */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Basic Information
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={8}>
                      <TextField
                        fullWidth
                        label="Service Title"
                        value={formData.title}
                        onChange={handleChange('title')}
                        error={!!errors.title}
                        helperText={errors.title}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Duration (minutes)"
                        type="number"
                        value={formData.duration}
                        onChange={handleChange('duration')}
                        error={!!errors.duration}
                        helperText={errors.duration}
                        required
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        multiline
                        rows={4}
                        value={formData.description || ''}
                        onChange={handleChange('description')}
                        error={!!errors.description}
                        helperText={errors.description}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Short Description"
                        multiline
                        rows={4}
                        value={formData.shortDescription || ''}
                        onChange={handleChange('shortDescription')}
                        error={!!errors.shortDescription}
                        inputProps={{ maxLength: 150 }}
                        helperText={
                          errors.shortDescription ||
                          `Note: Only accept 130 - 150 characters only (${(formData.shortDescription || '').length}/150)`
                        }
                        FormHelperTextProps={{
                          sx: {
                            color: 'error.main',
                            fontWeight: 500,
                          },
                        }}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={`Price (${DEFAULT_CURRENCY})`}
                        type="number"
                        value={formData.price}
                        onChange={handleChange('price')}
                        error={!!errors.price}
                        helperText={errors.price}
                        required
                        InputProps={{
                          startAdornment: (
                            <Typography sx={{ mr: 1 }}>
                              {DEFAULT_CURRENCY}
                            </Typography>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label={`Sort Order`}
                        type="number"
                        value={formData.sortOrder}
                        onChange={handleChange('sortOrder')}
                        error={!!errors.sortOrder}
                        helperText={errors.sortOrder}
                        placeholder="0"
                      />
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Box component="form" onSubmit={handleSubmit}>
                  <Divider sx={{ my: 3 }} />

                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      startIcon={<Cancel />}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={
                        saving ? <CircularProgress size={20} /> : <Save />
                      }
                      disabled={saving}
                      sx={{
                        background:
                          'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                        '&:hover': {
                          background:
                            'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                        },
                      }}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Service</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{formData.title}"? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditService;
